/* ============================================================
   Scroll controller for sections/custom-product-animation.liquid.

   The section is one tall element with a sticky stage inside it: while
   the stage is pinned, scroll position through the section is converted
   into a continuous "block float" (0 → N) that drives both the copy
   layer (groovy-can-ui.js) and the 3D layer (groovy-can-scene.js), then
   smoothed so scrubbing feels inertial.

   N comes from however many .section elements the section rendered, so
   adding, reordering or removing a product block is a theme-editor
   change, never a JS change.

   Ported from the standalone Three.js prototype, which owned the whole
   window. Three things had to change for a theme:
     · scroll is measured against the section, not window.scrollY, since
       the section sits between other sections on the page
     · the 6MB of .glb is only fetched once the section is close to the
       viewport — a merchant can put this anywhere on a page
     · the frame loop is parked whenever the section is off-screen
   ============================================================ */

/* Version queries on these two imports, and they are not decoration.
   Only assets Liquid loads through `asset_url` get a ?v=<hash> — everything
   reached by a RELATIVE import is requested at a URL that never changes, and
   Shopify's CDN pins those for days. Measured on the live store: this file's
   own edits landed while groovy-can-scene.js came back with age 529251s (6.1
   days) and none of the change in it. The browser then runs a new entry
   against stale modules, which fails as a TypeError on whatever the new code
   expects and the old code has not got.

   BUMP THE NUMBER whenever the imported file changes. The specifier has to be
   byte-identical everywhere the same module is imported (groovy-can-scene.js
   imports the config too) or the browser instantiates the module twice and
   each copy gets its own CAN object.

   three.module.js and friends are deliberately left unversioned: they have not
   changed, and a mismatched specifier there would pull a second megabyte of
   three.js into a second module registry. */
import { CAN } from './groovy-can-config.js?v=3';
import { buildUI } from './groovy-can-ui.js';
import { createScene } from './groovy-can-scene.js?v=10';
import { clamp } from './groovy-can-anim.js';

const SMOOTHING = 0.11; // scroll lerp; lower = heavier

/* Phone layout (below 750px, the section's own breakpoint): the stage is not
   pinned and a tab per drink drives the same block float instead of scroll.
   A tab tweens the float to that drink's HOLD point, so it plays exactly the
   copy sweep and can swap a scroll-through would, just on a clock. */
const MOBILE = window.matchMedia('(max-width: 749px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const HOLD = 0.55; // every TIMING window is fully in between 0.39 and 0.70
const TWEEN_MS = 1000; // per drink travelled
const AUTO_MS = 3000; // auto-advance; a tap jumps there and the rotation carries on from it

const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const wrap = (v, n) => ((v % n) + n) % n;

const instances = new Map();

function createExperience(root) {
  const stage = root.querySelector('[data-groovy-stage]');
  const overlay = root.querySelector('[data-groovy-overlay]');
  const canvas = root.querySelector('[data-groovy-canvas]');
  const loaderEl = root.querySelector('[data-groovy-loader]');
  const loaderBar = root.querySelector('[data-groovy-loader-bar]');
  const hint = root.querySelector('[data-groovy-hint]');
  const tabList = root.querySelector('[data-groovy-tabs]');
  if (!stage || !overlay || !canvas) return null;

  // The copy layer is markup-driven: buildUI enhances what Liquid already
  // rendered and reports back each block's model URL for the 3D layer.
  const sections = buildUI(overlay);
  if (!sections.length) return null;

  // Scroll length per product, in viewport heights — a section setting,
  // so a merchant can make the sequence linger or move briskly.
  const sectionVh = Number(root.dataset.sectionVh) || 1.7;

  let scene = null;
  let target = 0; // block float, straight from scroll position
  let current = 0; // smoothed
  let ready = false;
  let running = false;
  let rafId = null;
  let started = false;
  let hintHidden = false;
  let clockStart = performance.now();

  // tab mode (phone)
  let tabMode = false;
  let active = 0; // drink the tabs say is showing
  let tween = null; // { from, to, start, dur }
  let introDone = false;
  let shownReady = false; // models in, or gave up and fell back
  let autoStopped = REDUCED.matches;
  let hovered = false;
  let focused = false;
  let inView = false;
  let idle = 0;
  let lastFrame = 0;

  /* ---------- sizing ----------
     Height is written in px rather than left as a vh calculation, and the
     viewport height is only re-read when the width actually changes: on
     mobile the address bar collapsing fires resize constantly, and letting
     that resize the scroll track mid-scroll makes the whole sequence jump. */

  let viewportH = window.innerHeight;
  let viewportW = window.innerWidth;

  function sectionPx() {
    return sectionVh * viewportH;
  }

  function sizeTrack() {
    if (tabMode) return;
    // N blocks of scrolling, plus one viewport so the last one can exit
    root.style.height = `${sections.length * sectionPx() + viewportH}px`;
    readScroll();
  }

  function readScroll() {
    if (tabMode) return;
    // -rect.top is how far the section has scrolled past the top of the
    // viewport, which is exactly the distance the sticky stage is pinned for.
    const scrolled = -root.getBoundingClientRect().top;
    target = clamp(scrolled / sectionPx(), 0, sections.length);
  }

  function onResize() {
    if (window.innerWidth === viewportW) return;
    viewportW = window.innerWidth;
    viewportH = window.innerHeight;
    if (tabMode) return;
    sizeTrack();
    current = target;
  }

  /* ---------- tabs (phone) ---------- */

  const tabs = [];
  if (tabList) {
    const uid = stage.id || `groovy-${Math.random().toString(36).slice(2, 8)}`;
    sections.forEach((section, i) => {
      const template = section.el.nextElementSibling;
      if (!template || !template.matches('template[data-groovy-tab]')) return;
      const tab = template.content.firstElementChild.cloneNode(true);
      tab.id = `${uid}-tab-${i}`;
      tab.dataset.index = i;
      tab.setAttribute('aria-controls', stage.id);
      // A tap jumps to that drink and restarts the countdown from there; the
      // rotation keeps going rather than stopping for good.
      tab.addEventListener('click', () => {
        goTo(i);
      });
      tabList.appendChild(tab);
      tabs.push(tab);
    });

    // Arrow keys move along the row and select as they go (automatic
    // activation), Home/End jump to either end.
    tabList.addEventListener('keydown', event => {
      const at = tabs.indexOf(document.activeElement);
      if (at < 0) return;
      let next = null;
      if (event.key === 'ArrowRight') next = (at + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (at - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      tabs[next].focus();
      tabs[next].click();
    });
  }

  function markTabs() {
    let selected = null;
    for (const tab of tabs) {
      const on = Number(tab.dataset.index) === active;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on) selected = tab;
    }
    if (!selected && tabs[0]) tabs[0].tabIndex = 0;
    if (tabMode && selected) stage.setAttribute('aria-labelledby', selected.id);
  }

  /* Tween the float to drink j's hold point the short way round, so no other
     drink flashes past: forwards through the hand-off scroll plays, or
     backwards like scrolling up. Auto-advance always goes forwards, wrapping
     from the last drink through an exit straight into the first. */
  function goTo(j, { forward = false } = {}) {
    const n = sections.length;
    introDone = true;
    idle = 0;
    active = j;
    markTabs();

    if (REDUCED.matches) {
      tween = null;
      current = target = j + HOLD;
      return;
    }

    let d = wrap(j + HOLD - current, n);
    if (!forward && d > n / 2) d -= n;
    if (Math.abs(d) < 0.0005) return;
    const from = current;
    tween = {
      from,
      to: from + d,
      start: performance.now(),
      dur: clamp(TWEEN_MS * Math.abs(d), 900, 1800)
    };
  }

  function stepTabs(now) {
    const dt = lastFrame ? Math.min(now - lastFrame, 100) : 0;
    lastFrame = now;

    if (tween) {
      const s = clamp((now - tween.start) / tween.dur, 0, 1);
      current = tween.from + (tween.to - tween.from) * easeInOut(s);
      if (s >= 1) {
        current = wrap(tween.to, sections.length);
        tween = null;
      }
      target = current;
      return;
    }

    if (autoStopped || hovered || focused || !inView || !shownReady || REDUCED.matches) return;
    idle += dt;
    if (idle >= AUTO_MS) goTo((active + 1) % sections.length, { forward: true });
  }

  function applyMode(event) {
    tabMode = MOBILE.matches;
    root.classList.toggle('is-tabbed', tabMode);
    tween = null;
    if (tabMode) {
      root.style.height = '';
      stage.setAttribute('role', 'tabpanel');
      // before the models are in, hold at 0 so the copy sweeps in with the can
      current = target = introDone || shownReady ? active + HOLD : 0;
    } else {
      stage.removeAttribute('role');
      stage.removeAttribute('aria-labelledby');
      sizeTrack();
      // on boot the float eases up from 0 as it always has; only a live
      // switch from the phone layout snaps straight to the scroll position
      if (event) current = target;
    }
    markTabs();
  }

  function intro() {
    if (tabMode && !introDone) goTo(active, { forward: true });
  }

  /* ---------- frame loop ---------- */

  function frame(now) {
    rafId = requestAnimationFrame(frame);
    const n = sections.length;

    if (tabMode) {
      stepTabs(now);
    } else {
      current += (target - current) * SMOOTHING;
      if (Math.abs(target - current) < 0.0002) current = target;
    }

    // tab mode runs the float past N (or below 0) to wrap round; N reads as 0
    const raw = tabMode ? wrap(current, n) : clamp(current, 0, n);
    const index = Math.min(Math.floor(raw), n - 1);
    const p = raw - index;

    for (let i = 0; i < n; i++) {
      // blocks behind the playhead are held fully exited, ahead fully unborn
      const sp = i === index ? p : i < index ? 1 : 0;
      sections[i].update(sp, i === index);
    }

    if (hint) {
      if (!hintHidden && raw > 0.06) {
        hint.classList.add('is-hidden');
        hintHidden = true;
      } else if (hintHidden && raw <= 0.06) {
        hint.classList.remove('is-hidden');
        hintHidden = false;
      }
    }

    if (ready) scene.update(raw, (performance.now() - clockStart) / 1000);
  }

  function play() {
    if (running) return;
    running = true;
    lastFrame = 0;
    readScroll();
    rafId = requestAnimationFrame(frame);
  }

  function pause() {
    if (!running) return;
    running = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
  }

  /* ---------- boot ----------
     The models are several MB; they are only fetched once the section is
     within a couple of viewports, so a merchant can drop this low on a
     page without it costing anything above the fold. */

  function start() {
    if (started) return;
    started = true;

    try {
      scene = createScene(canvas, sections, stage, overlay);
    } catch (error) {
      // No WebGL (or the context was refused) — the copy layer still
      // scrubs; the stage falls back to each block's product image.
      console.error('[groovy] could not create the 3D stage', error);
      fallback();
      return;
    }

    scene
      .load(t => {
        if (loaderBar) loaderBar.style.width = `${Math.round(t * 100)}%`;
      })
      .then(() => {
        if (loaderBar) loaderBar.style.width = '100%';
        ready = true;
        clockStart = performance.now();
        readScroll();
        if (!tabMode) current = target;

        // Clear the loader first, then let the can spin itself in —
        // otherwise the reveal burst plays behind an opaque overlay.
        setTimeout(() => loaderEl?.classList.add('is-done'), 250);
        setTimeout(() => {
          scene.reveal();
          shownReady = true;
          intro();
        }, 420);
      })
      .catch(error => {
        console.error('[groovy] model load failed', error);
        fallback();
      });
  }

  function fallback() {
    root.classList.add('is-fallback');
    loaderEl?.classList.add('is-done');
    shownReady = true;
    intro();
  }

  const preloader = new IntersectionObserver(
    entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      preloader.disconnect();
      start();
    },
    { rootMargin: '200% 0px' }
  );

  const visibility = new IntersectionObserver(
    entries => {
      if (entries.some(entry => entry.isIntersecting)) play();
      else pause();
    },
    { rootMargin: '10% 0px' }
  );

  // Auto-advance only counts while most of the stage is on screen.
  const onScreen = new IntersectionObserver(
    entries => {
      inView = entries.some(entry => entry.isIntersecting);
    },
    { threshold: 0.5 }
  );

  const onEnter = event => {
    if (event.pointerType === 'mouse') hovered = true;
  };
  const onLeave = event => {
    if (event.pointerType === 'mouse') hovered = false;
  };
  // Only keyboard focus holds the rotation. A tap also focuses the tab, and
  // pausing on that would stop it for good on a phone.
  const onFocusIn = event => {
    focused = event.target.matches(':focus-visible');
  };
  const onFocusOut = event => {
    if (!root.contains(event.relatedTarget)) focused = false;
  };
  const onReducedChange = () => {
    if (REDUCED.matches) autoStopped = true;
  };

  applyMode();
  preloader.observe(root);
  visibility.observe(root);
  onScreen.observe(stage);
  window.addEventListener('scroll', readScroll, { passive: true });
  window.addEventListener('resize', onResize);
  MOBILE.addEventListener('change', applyMode);
  REDUCED.addEventListener('change', onReducedChange);
  root.addEventListener('pointerenter', onEnter);
  root.addEventListener('pointerleave', onLeave);
  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('focusout', onFocusOut);

  function destroy() {
    pause();
    preloader.disconnect();
    visibility.disconnect();
    onScreen.disconnect();
    window.removeEventListener('scroll', readScroll);
    window.removeEventListener('resize', onResize);
    MOBILE.removeEventListener('change', applyMode);
    REDUCED.removeEventListener('change', onReducedChange);
    scene?.destroy();
  }

  // live handle for tuning from the console: __groovy[0].CAN.rockAmp = 0.3
  return { root, sections, destroy, CAN, get scene() { return scene; } };
}

function boot(container) {
  (container || document).querySelectorAll('[data-groovy-experience]').forEach(root => {
    if (instances.has(root)) return;
    const instance = createExperience(root);
    if (instance) instances.set(root, instance);
  });
  window.__groovy = [...instances.values()];
}

function teardown(container) {
  instances.forEach((instance, root) => {
    if (container && !container.contains(root)) return;
    instance.destroy();
    instances.delete(root);
  });
  window.__groovy = [...instances.values()];
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

// The theme editor swaps a section's markup in place, which leaves a dead
// WebGL context and a stale scroll listener behind unless we hand them back.
document.addEventListener('shopify:section:load', event => boot(event.target));
document.addEventListener('shopify:section:unload', event => teardown(event.target));
